import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Question } from '../services/QuestionStore';
import { ShowQuestion } from './ShowQuestion';
import { Card } from 'primereact/card';
import { QuestionnaireService } from '../services/QuestionnaireService';
import PartnerPage from '../pages/PartnerPage';
import { ProgressSpinner } from 'primereact/progressspinner';

export const FillQuestionare: React.FC = () => {
    const { id: tokenId } = useParams<{ id: string }>();
    const [currentQuestion, setCurrentQuestion] = useState<Question[] | null>(null);
    const [index, setIndex] = useState(0);
    const [total, setTotal] = useState(0);
    const [readOnly, setReadOnly] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState<number | undefined>(undefined);

    const loadNextQuestion = async () => {
        try {
            const nextQuestion = await QuestionnaireService.getNextQuestion(tokenId!);
            setCurrentQuestion(nextQuestion.nextQuestions);
            setIndex(nextQuestion.index);
            setTotal(nextQuestion.total);
            setRemainingSeconds(nextQuestion.remainingSeconds);
        } catch (error) {
            console.error('Failed to load next question', error);
        }
    };

    useEffect(() => {
        loadNextQuestion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAnswer = async (question: Question, answer: string) => {
        setReadOnly(true);
        try {
            const nextQuestion = await QuestionnaireService.submitAnswer(tokenId!, question.id!, answer);
            setCurrentQuestion(nextQuestion.nextQuestions);
            setIndex(nextQuestion.index);
            setTotal(nextQuestion.total);
            setRemainingSeconds(nextQuestion.remainingSeconds);
        } catch (error) {
            console.error('Failed to submit answer', error);
        }
        setReadOnly(false);
    };

    useEffect(() => {
        if (remainingSeconds === undefined) return;

        const timer = setInterval(() => {
            setRemainingSeconds(prev => {
                if (prev === undefined) return prev;
                if (prev <= 1) {
                    // expire: clear interval and refresh questions
                    clearInterval(timer);
                    setTimeout(() => loadNextQuestion(), 1000);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [remainingSeconds]);

    const title = currentQuestion ?
        index + 1 === total ? ""
            : (index + 1 || 1) + "/" + total
        : "Loading...";

    return !currentQuestion ? (
        <div className="flex justify-content-center align-items-center" style={{ height: 'calc(100vh - 200px)' }}>
            <ProgressSpinner />
        </div>
    ) : (
        <>
            <PartnerPage
                title={title}
                end={remainingSeconds !== undefined ? <TimerDisplay remainingSeconds={remainingSeconds} /> : undefined}
            />
            {currentQuestion && currentQuestion.map((q, i) => (
                <div className='flex justify-content-center' key={q.id ?? i}>
                    <Card className='mt-8 m-2 md:m-4 md:mt-8' style={{ width: '80vw', minHeight: "8rem", maxWidth: '800px' }}>
                        <ShowQuestion
                            question={q}
                            onAnswer={(answer) => handleAnswer(q, answer)}
                            readOnly={readOnly}
                        />
                    </Card >
                </div>
            ))
            }
        </>
    );
};


const TimerDisplay: React.FC<{ remainingSeconds?: number }> = ({ remainingSeconds }) => {
    if (remainingSeconds === undefined) return null;
    const mm = Math.floor(remainingSeconds / 60).toString().padStart(2, '0');
    const ss = (remainingSeconds % 60).toString().padStart(2, '0');
    return <div className="mr-3" style={{ fontWeight: 600, color: 'var(--primary-color-text)' }}>{mm}:{ss}</div>;
};

